import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/api/query-keys'
import {
  flushScheduledAlbumPhotosRefresh,
  scheduleAlbumPhotosRefresh,
} from '@/lib/photos/schedule-album-photos-refresh'

/**
 * Re-fetches the roster (debounced) after the session's placements change, so the server's
 * placed flag catches up with the overrides laid over it, as `useClikArtPlacementRefresh` does for
 * ClikArt. It runs for any change to the placement snapshot, undo and redo included, not only the
 * event a fresh placement raises. A refresh still waiting when the tray unmounts is flushed.
 */
export function usePortraitPlacementRefresh(placementOverrides: ReadonlyMap<string, boolean>) {
  const queryClient = useQueryClient()
  // The snapshot the tray mounted with is not a change: only a later one schedules a refresh.
  const seenOverridesRef = useRef(placementOverrides)

  useEffect(() => {
    if (seenOverridesRef.current === placementOverrides) {
      return
    }

    seenOverridesRef.current = placementOverrides
    scheduleAlbumPhotosRefresh(queryClient, queryKeys.portraits.allStudents())
  }, [placementOverrides, queryClient])

  useEffect(() => {
    const queryKey = queryKeys.portraits.allStudents()

    return () => flushScheduledAlbumPhotosRefresh(queryKey)
  }, [])
}
